import React, { useState, useEffect, useCallback } from 'react';
import { Card, Input, Select, DatePicker, Textarea, useToast, Spinner, RNLLoader, Modal, Btn, ReturnDestinationNote } from '../UI';
import * as api from '../../api';
import { calcItemAmount, calcItemVat, calcTotals, fmt } from './mpoUtils';
import { useUOM } from '../../hooks/useUOM';

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoBack   = () => <svg {...ic}><polyline points="15 18 9 12 15 6"/></svg>;
const IcoPlus   = () => <svg {...ic}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoTrash  = () => <svg {...ic}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IcoSave   = () => <svg {...ic}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const IcoSend   = () => <svg {...ic}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const IcoX      = () => <svg {...ic}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoCheck  = () => <svg {...ic}><polyline points="20 6 9 17 4 12"/></svg>;

// ─── Constants ────────────────────────────────────────────────────────────────
const BLANK_ITEM = {
  material_id: null, material_code: '', material_description: '',
  new_item_description: '', specification: '',
  warehouse: '', request_ref: '', boq_item_code: '',
  rem_rqst_qty: '', last_pur_pr: '', disc: '',
  unit_of_measure: '', quantity_ordered: '', unit_rate: '', amount: '',
  vat_percentage: '7.5', vat_amount: '', remarks: '',
  procurement_request_id: null, material_request_id: null, lineitem_id: null, sub_item_id: null,
};

const CURRENCY_OPTIONS = [
  { value: 'NGN', label: 'NGN — Nigerian Naira' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
];

const CATEGORY_OPTIONS = [
  { value: 'local',  label: 'Local' },
  { value: 'import', label: 'Import' },
];


const PAYMENT_TERMS_OPTIONS = [
  { value: 'Net 30',                        label: 'Net 30' },
  { value: 'Net 45',                        label: 'Net 45' },
  { value: 'Net 60',                        label: 'Net 60' },
  { value: '30% Advance / 70% on Delivery', label: '30% Advance / 70% on Delivery' },
  { value: '50% Advance / 50% on Delivery', label: '50% Advance / 50% on Delivery' },
  { value: '70% Advance / 30% on Delivery', label: '70% Advance / 30% on Delivery' },
  { value: '100% Advance',                  label: '100% Advance' },
  { value: 'Payment on Delivery',           label: 'Payment on Delivery' },
  { value: 'LC at Sight',                   label: 'LC at Sight' },
  { value: '30/45 Days Credit',             label: '30/45 Days Credit' },
  { value: '20/80',                         label: '20/80' },
];

// ─── Section header (matches MR form) ────────────────────────────────────────
function SectionHeader({ title }) {
  return (
    <div style={{
      fontWeight: 800, fontSize: 11, color: '#1e3a8a',
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1e3a8a', flexShrink: 0 }} />
      {title}
    </div>
  );
}

// ─── Success modal ────────────────────────────────────────────────────────────
function SuccessModal({ submitted, poNumber, onOk, backTo }) {
  const title    = submitted ? (backTo ? 'Resubmitted' : 'Submitted for Approval') : 'Saved as Draft';
  const subtitle = submitted
    ? (backTo ? `The corrected Purchase Order has gone back to ${backTo}.` : 'The Purchase Order has been submitted and is now awaiting approval.')
    : 'The Purchase Order has been saved as a draft. You can edit and submit it later.';
  return (
    <Modal hideHeader width={420} onClose={onOk}>
      <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: submitted ? '#dcfce7' : '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: submitted ? '0 0 0 8px #f0fdf4' : '0 0 0 8px #eff6ff' }}>
          {submitted
            ? <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          }
        </div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{title}</h3>
        <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{subtitle}</p>
        {poNumber && (
          <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, color: '#1e3a8a', marginBottom: 20, letterSpacing: '0.03em' }}>
            PO#: {poNumber}
          </div>
        )}
        <button onClick={onOk} style={{ background: submitted ? 'linear-gradient(135deg,#16a34a 0%,#15803d 100%)' : 'linear-gradient(135deg,#1e3a8a 0%,#1e40af 100%)', color: '#fff', border: 'none', padding: '10px 40px', borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          {submitted ? 'Great, Thanks!' : 'OK'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Confirm submit modal ─────────────────────────────────────────────────────
function ConfirmModal({ saving, totals, onCancel, onConfirm, stepInfo, backTo, returnReason }) {
  const approverMode = stepInfo?.approver_mode;
  const stepName     = stepInfo?.step_name;
  const users        = stepInfo?.eligible_users || [];

  return (
    <Modal title={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><IcoSend /> {backTo ? 'Confirm Resubmission' : 'Confirm Submission'}</span>} onClose={saving ? () => {} : onCancel} width={500}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
        {backTo
          ? <>You are about to <strong>resubmit</strong> this corrected Purchase Order. It goes back to the step that returned it.</>
          : <>You are about to submit this Purchase Order for <strong>approval</strong>. Once submitted it cannot be edited until reviewed.</>}
      </p>
      {backTo && returnReason && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12.5, color: '#9a3412' }}>
          <b>Returned because:</b> {returnReason}
        </div>
      )}

      {/* Totals summary */}
      <div style={{ background: '#f1f5f9', borderRadius: 10, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>Subtotal</span>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{fmt(totals.subtotal_amount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>VAT</span>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{fmt(totals.vat_amount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>Grand Total</span>
          <span style={{ fontWeight: 800, color: '#0b318bff' }}>{fmt(totals.grand_total)}</span>
        </div>
      </div>

      {/* Next Approval Step — same pattern as PRDetailsView; a resubmission goes back to the step that returned it */}
      {backTo ? (
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Goes back to - {backTo}</label>
          <ReturnDestinationNote stepName={backTo} />
        </div>
      ) : (
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
          {stepName ? `Next Approval Step — ${stepName}` : 'Next Approval Step'}
        </label>
        {approverMode === 'specific_user' && users.length > 0 ? (
          <Select
            value={users[0].user_id || users[0].id}
            onChange={() => {}}
            options={users.map(a => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
          />
        ) : approverMode && approverMode !== 'is_final' ? (
          <Select
            value="step_queue"
            onChange={() => {}}
            options={[{ value: 'step_queue', label: `🏢 ${stepName || 'Management Approval'}` }]}
          />
        ) : !approverMode ? (
          <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
            ⚠ No approver configured — contact your administrator.
          </div>
        ) : null}
      </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn variant="outline" disabled={saving} onClick={onCancel}>Cancel</Btn>
        <Btn disabled={saving} onClick={onConfirm} style={{ background: saving ? '#86efac' : '#16a34a', color: '#fff', border: 'none' }}>
          {saving ? 'Submitting…' : (backTo ? `Yes, Resubmit to ${backTo}` : 'Yes, Submit for Approval')}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Table cell input ─────────────────────────────────────────────────────────
function CellInput({ value, onChange, placeholder, type = 'text', readOnly, style: s }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '6px 9px', fontSize: 12,
        border: readOnly ? '1.5px solid #e2e8f0' : '1px solid #cbd5e1',
        borderRadius: 7,
        background: readOnly ? '#f8fafc' : '#fff',
        color: readOnly ? '#64748b' : '#1e293b',
        outline: 'none', fontFamily: 'var(--font)',
        ...s,
      }}
    />
  );
}

// ─── Main form ────────────────────────────────────────────────────────────────
export default function MPOForm({ existing, initSupplier, initProject, onSaved, onCancel }) {
  const isEdit = !!existing;
  // a returned PO is resubmitted to the approval step that returned it, not submitted afresh
  const backTo = existing?.status === 'returned' ? (existing.returned_from_step_name || 'the step that returned it') : null;
  const toast  = useToast();
  const { uomOptions: UOM_OPTIONS } = useUOM();

  const [projects,     setProjects]     = useState([]);
  const [suppliers,    setSuppliers]    = useState([]);
  const [materials,    setMaterials]    = useState([]);
  const [me,           setMe]           = useState(null);
  const [loadingMeta,  setLoadingMeta]  = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [prefetching,     setPrefetching]     = useState(false);
  const [prefetchedStep,  setPrefetchedStep]  = useState(null);
  const [confirmSubmit,   setConfirmSubmit]   = useState(false);
  const [successModal,    setSuccessModal]    = useState(null);

  const [header, setHeader] = useState({
    po_number:              existing?.po_number              || '',
    project_id:             existing?.project_id             || initProject  || '',
    supplier_id:            existing?.supplier_id            || initSupplier || '',
    po_date:                existing?.po_date                || '',
    expected_delivery_date: existing?.expected_delivery_date || '',
    delivery_date:          existing?.delivery_date          || '',
    delivery_address:       existing?.delivery_address       || '',
    payment_terms:          existing?.payment_terms          || '',
    currency:               existing?.currency               || 'NGN',
    notes:                  existing?.notes                  || '',
    internal_remarks:       existing?.internal_remarks       || '',
    category:               existing?.category               || '',
    delivery_terms:         existing?.delivery_terms         || '',
    charges:                existing?.charges != null ? String(existing.charges) : '',
    is_down_payment:        existing?.is_down_payment        || false,
    down_payment:           existing?.down_payment != null ? String(existing.down_payment) : '',
  });

  const [items, setItems] = useState(() => {
    const existing_items = existing?.items || [];
    if (existing_items.length > 0) {
      return existing_items.map(it => ({
        ...BLANK_ITEM, ...it, item_id: it.item_id,
        quantity_ordered: String(it.quantity_ordered ?? ''),
        unit_rate:        String(it.unit_rate ?? ''),
        amount:           String(it.amount ?? ''),
        vat_percentage:   it.vat_percentage != null ? String(it.vat_percentage) : '7.5',
        vat_amount:       it.vat_amount     != null ? String(it.vat_amount)     : '',
        rem_rqst_qty:     it.rem_rqst_qty   != null ? String(it.rem_rqst_qty)   : '',
        last_pur_pr:      it.last_pur_pr    != null ? String(it.last_pur_pr)    : '',
        disc:             it.disc           != null ? String(it.disc)           : '',
      }));
    }
    return [{ ...BLANK_ITEM }];
  });

  useEffect(() => {
    setLoadingMeta(true);
    Promise.all([
      api.listProjects(0).catch(() => ({ projects: [] })),
      api.listSuppliers(false, 0, 1000).catch(() => []),
      api.listMaterials(0, 1000).catch(() => ({ items: [] })),
    ]).then(([pr, sup, mats]) => {
      setProjects((pr?.projects || pr || []).map(p => ({ value: p.project_id || p.id, label: `${p.project_code} — ${p.project_name || p.name}` })));
      setSuppliers((Array.isArray(sup) ? sup : sup?.suppliers || []).map(s => ({ value: s.supplier_id || s.id, label: s.name })));
      const matList = Array.isArray(mats) ? mats : (mats?.items || mats?.materials || []);
      setMaterials(matList.map(m => ({
        value: m.material_id,
        label: `${m.code} — ${m.name}`,
        code: m.code,
        name: m.name,
        uom: m.unit_of_measure,
        rate: m.standard_rate,
      })));
    }).finally(() => setLoadingMeta(false));
    api.getMe().then(setMe).catch(() => {});
  }, []);

  const setH = (k) => (v) => setHeader(h => ({ ...h, [k]: v }));

  const requesterName = me?.full_name || (me?.first_name && `${me.first_name} ${me.last_name || ''}`.trim()) || me?.username || '—';

  // ── Item helpers ──────────────────────────────────────────────────────────
  const updateItem = useCallback((idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [field]: value };
      if (field === 'material_id') {
        const mat = materials.find(m => m.value === value);
        if (mat) {
          next.material_code = mat.code || '';
          next.material_description = mat.name || '';
          if (mat.uom) next.unit_of_measure = mat.uom;
          if (mat.rate != null) next.last_pur_pr = String(mat.rate);
        }
      }
      if (field === 'quantity_ordered' || field === 'unit_rate' || field === 'disc') {
        const qty  = field === 'quantity_ordered' ? value : it.quantity_ordered;
        const rate = field === 'unit_rate'        ? value : it.unit_rate;
        const disc = field === 'disc'             ? value : it.disc;
        const amt  = calcItemAmount(qty, rate, disc);
        next.amount = String(amt);
        if (next.vat_percentage) next.vat_amount = String(calcItemVat(amt, next.vat_percentage));
      }
      if (field === 'vat_percentage') {
        const amt = parseFloat(next.amount || 0);
        next.vat_amount = value ? String(calcItemVat(amt, value)) : '';
      }
      return next;
    }));
  }, [materials]);

  const addItem    = () => setItems(prev => [...prev, { ...BLANK_ITEM }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const totals = calcTotals(items);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (submitAfterSave = false) => {
    if (!header.project_id)  return toast.show('Please select a project.', 'error');
    if (!header.supplier_id) return toast.show('Please select a supplier.', 'error');
    // the typed PO number may wait on a draft; it is needed to submit
    if (submitAfterSave && !header.po_number?.trim()) return toast.show('Enter the PO number before submitting.', 'error');
    if (items.length === 0)  return toast.show('Add at least one line item.', 'error');
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.material_description?.trim()) return toast.show(`Line ${i + 1}: Material description is required.`, 'error');
      if (!parseFloat(it.quantity_ordered)) return toast.show(`Line ${i + 1}: Quantity must be > 0.`, 'error');
      if (parseFloat(it.unit_rate) < 0)     return toast.show(`Line ${i + 1}: Unit rate cannot be negative.`, 'error');
    }

    const payload = {
      ...header,
      ...totals,
      po_number:      header.po_number?.trim() || null,
      requested_by:   me?.user_id || me?.id || null,
      charges:        header.charges        ? parseFloat(header.charges)        : null,
      down_payment:   header.down_payment   ? parseFloat(header.down_payment)   : null,
      is_down_payment: header.is_down_payment,
      items: items.map(it => ({
        // existing lines are updated in place, keeping Cost Control's and Management's review on them
        item_id:                 it.item_id                 || null,
        material_id:             it.material_id             || null,
        material_code:           it.material_code           || null,
        material_description:    it.material_description    || null,
        new_item_description:    it.new_item_description    || null,
        specification:           it.specification           || null,
        warehouse:               it.warehouse               || null,
        request_ref:             it.request_ref             || null,
        boq_item_code:           it.boq_item_code           || null,
        rem_rqst_qty:            it.rem_rqst_qty  ? parseFloat(it.rem_rqst_qty)  : null,
        last_pur_pr:             it.last_pur_pr   ? parseFloat(it.last_pur_pr)   : null,
        disc:                    it.disc          ? parseFloat(it.disc)          : null,
        unit_of_measure:         it.unit_of_measure || null,
        quantity_ordered:        parseFloat(it.quantity_ordered),
        unit_rate:               parseFloat(it.unit_rate   || 0),
        amount:                  parseFloat(it.amount      || 0),
        vat_percentage:          it.vat_percentage ? parseFloat(it.vat_percentage) : null,
        vat_amount:              it.vat_amount     ? parseFloat(it.vat_amount)     : null,
        remarks:                 it.remarks        || null,
        procurement_request_id:  it.procurement_request_id || null,
        material_request_id:     it.material_request_id    || null,
        lineitem_id:             it.lineitem_id             || null,
        sub_item_id:             it.sub_item_id             || null,
      })),
    };

    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await api.updateMPO(existing.po_id, payload);
      } else {
        saved = await api.createMPO(payload);
      }
      if (submitAfterSave) {
        const poId = saved?.po_id || existing?.po_id;
        await api.submitMPO(poId, {});
      }
      setConfirmSubmit(false);
      setPrefetchedStep(null);
      setSuccessModal({ submitted: submitAfterSave, poNumber: saved?.po_number || saved?.serial_number || existing?.po_number || existing?.serial_number });
    } catch (err) {
      toast.show(err?.response?.data?.detail || err.message || 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loadingMeta) {
    return <RNLLoader title="Loading PO Form…" subtitle="Preparing your purchase order" />;
  }

  const thS = {
    padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 10,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#fff',
    whiteSpace: 'nowrap', background: '#1e3a8a',
    border: '1px solid rgba(255,255,255,0.18)',
  };
  const tdS = { padding: '6px 8px', border: '1px solid #e2e8f0', verticalAlign: 'middle', overflow: 'hidden' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Modals */}
      {successModal && (
        <SuccessModal
          submitted={successModal.submitted}
          poNumber={successModal.poNumber}
          backTo={successModal.submitted ? backTo : null}
          onOk={() => { setSuccessModal(null); onSaved?.(); }}
        />
      )}
      {confirmSubmit && (
        <ConfirmModal
          stepInfo={prefetchedStep}
          backTo={backTo}
          returnReason={existing?.return_reason}
          saving={saving}
          totals={totals}
          onCancel={() => { setConfirmSubmit(false); setPrefetchedStep(null); }}
          onConfirm={() => handleSave(true)}
        />
      )}

      {/* Back + title */}
      <div>
        <button type="button" onClick={onCancel} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#0b318bff', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 16, padding: '9px 18px' }}>
          <IcoBack /> Back
        </button>
        <h2 style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>
          {isEdit ? `Edit PO — ${existing.po_number || existing.serial_number}${existing.po_number ? ` (Serial ${existing.serial_number})` : ''}` : 'New Material Purchase Order'}
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          Fill in the PO header, add line items, then save as draft or submit for approval.
        </p>
      </div>

      {/* ── Section 1 — PO Header ─────────────────────────────────────────── */}
      <Card style={{ padding: '18px 22px' }}>
        <SectionHeader title="Section 1 — PO Header" />

        {/* Row 1 — PO Number (typed by the requester; the serial number is the system's own) | Requested By | Project | Supplier | Currency | PO Date */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, alignItems: 'end' }}>
          <Input label="PO NUMBER" value={header.po_number} onChange={setH('po_number')} placeholder="Type the PO number" maxLength={50} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Requested By
            </div>
            <div style={{
              padding: '9px 12px', borderRadius: 8,
              border: '1.5px solid #bfdbfe', background: '#eff6ff',
              fontSize: 13, fontWeight: 600, color: '#1d4ed8',
              height: 38, display: 'flex', alignItems: 'center',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              cursor: 'not-allowed',
            }}>
              {requesterName}
            </div>
          </div>
          <Select
            label="PROJECT *"
            title="Select Project"
            value={header.project_id}
            onChange={setH('project_id')}
            options={projects}
            disabled={isEdit}
          />
          <Select
            label="SUPPLIER *"
            title="Select Supplier"
            value={header.supplier_id}
            onChange={setH('supplier_id')}
            options={suppliers}
          />
          <Select
            label="CURRENCY"
            title="Select Currency"
            value={header.currency}
            onChange={setH('currency')}
            options={CURRENCY_OPTIONS}
          />
          <DatePicker label="PO DATE" value={header.po_date} onChange={setH('po_date')} />
        </div>

        {/* Row 2 — Dates | Payment Terms | Category | Delivery Terms */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginTop: 16, alignItems: 'end' }}>
          <DatePicker label="EXPECTED DELIVERY DATE" value={header.expected_delivery_date} onChange={setH('expected_delivery_date')} />
          <DatePicker label="DELIVERY DATE" value={header.delivery_date} onChange={setH('delivery_date')} />
          <Select
            label="PAYMENT TERMS"
            title="Select Payment Terms"
            value={header.payment_terms}
            onChange={setH('payment_terms')}
            options={PAYMENT_TERMS_OPTIONS}
          />
          <Select
            label="CATEGORY"
            title="Select Category"
            value={header.category}
            onChange={setH('category')}
            options={CATEGORY_OPTIONS}
          />
          <Input label="DELIVERY TERMS" value={header.delivery_terms} onChange={setH('delivery_terms')} placeholder="e.g. DAP, FOB, CIF" />
        </div>

        {/* Row 3 — Charges + Delivery Address side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 16, marginTop: 16, alignItems: 'end' }}>
          <Input
            label={`CHARGES (${header.currency || 'NGN'})`}
            value={header.charges}
            onChange={setH('charges')}
            placeholder="0.00"
            type="number"
          />
          <Input label="DELIVERY ADDRESS" value={header.delivery_address} onChange={setH('delivery_address')} placeholder="Delivery address…" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <Textarea label="NOTES" value={header.notes} onChange={setH('notes')} rows={2} />
          <Textarea label="INTERNAL REMARKS" value={header.internal_remarks} onChange={setH('internal_remarks')} rows={2} />
        </div>

        {/* Down Payment */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={header.is_down_payment}
              onChange={e => setHeader(h => ({ ...h, is_down_payment: e.target.checked, down_payment: e.target.checked ? h.down_payment : '' }))}
              style={{ width: 15, height: 15, accentColor: '#1e3a8a', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#1e3a8a' }}>
              Down Payment
            </span>
          </label>
          {header.is_down_payment && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{header.currency || 'NGN'}</span>
              <input
                type="text"
                inputMode="decimal"
                value={header.down_payment
                  ? parseFloat(header.down_payment).toLocaleString('en-NG', { maximumFractionDigits: 2 })
                  : header.down_payment}
                onChange={e => {
                  const raw = e.target.value.replace(/,/g, '');
                  if (raw === '' || /^\d*\.?\d*$/.test(raw)) setH('down_payment')(raw);
                }}
                placeholder="0.00"
                style={{
                  width: 180, padding: '6px 9px', fontSize: 13, fontWeight: 700,
                  border: '1.5px solid #1e3a8a', borderRadius: 7,
                  outline: 'none', textAlign: 'right', fontFamily: 'var(--font)',
                  color: '#1e3a8a',
                }}
              />
            </div>
          )}
          {!header.is_down_payment && (
            <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>= NO ADVANCE</span>
          )}
        </div>
      </Card>

      {/* ── Section 2 — Line Items ────────────────────────────────────────── */}
      <Card style={{ padding: '18px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <SectionHeader title={`Section 2 — Line Items (${items.length})`} />
          <button
            type="button"
            onClick={addItem}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', borderRadius: 7,
              border: '1.5px dashed #93c5fd', background: '#eff6ff',
              color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              marginBottom: 18,
            }}
          >
            <IcoPlus /> Add Line
          </button>
        </div>

        <div style={{ overflowX: 'auto', border: '1.5px solid #bfdbfe', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #1e3a8a' }}>
                <th style={{ ...thS, minWidth: 42,  textAlign: 'center' }}>#</th>
                <th style={{ ...thS, minWidth: 280, textAlign: 'center' }}>Item Code</th>
                <th style={{ ...thS, minWidth: 300, textAlign: 'center' }}>Description</th>
                <th style={{ ...thS, minWidth: 280, textAlign: 'center' }}>New Item Description</th>
                <th style={{ ...thS, minWidth: 240, textAlign: 'center' }}>Specification</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'center' }}>Warehouse</th>
                <th style={{ ...thS, minWidth: 220, textAlign: 'center' }}>Request Ref</th>
                <th style={{ ...thS, minWidth: 180, textAlign: 'center' }}>BOQ Code</th>
                <th style={{ ...thS, minWidth: 220, textAlign: 'center' }}>Material Code</th>
                <th style={{ ...thS, minWidth: 180, textAlign: 'center' }}>Rem. Rqst Qty</th>
                <th style={{ ...thS, minWidth: 170, textAlign: 'center' }}>Unit</th>
                <th style={{ ...thS, minWidth: 160, textAlign: 'center' }}>Qty *</th>
                <th style={{ ...thS, minWidth: 180, textAlign: 'center' }}>Last Pur.Pr</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'center' }}>Price</th>
                <th style={{ ...thS, minWidth: 140, textAlign: 'center' }}>Disc %</th>
                <th style={{ ...thS, minWidth: 140, textAlign: 'center' }}>VAT %</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'center' }}>Total</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'center' }}>Remark</th>
                <th style={{ ...thS, minWidth: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...tdS, color: '#94a3b8', fontSize: 11, textAlign: 'center', fontWeight: 700 }}>{idx + 1}</td>
                  {/* Item Code — select from material master */}
                  <td style={tdS}>
                    <Select
                      title="Select Material (Item Code)"
                      value={it.material_id}
                      onChange={v => updateItem(idx, 'material_id', v)}
                      options={materials}
                      placeholder="— Item Code —"
                      style={{ fontSize: 12, padding: '6px 9px' }}
                    />
                  </td>
                  {/* Description — auto-filled or manual */}
                  <td style={tdS}>
                    <CellInput
                      value={it.material_description}
                      onChange={e => updateItem(idx, 'material_description', e.target.value)}
                      placeholder="Description"
                    />
                  </td>
                  {/* New Item Description */}
                  <td style={tdS}>
                    <CellInput
                      value={it.new_item_description}
                      onChange={e => updateItem(idx, 'new_item_description', e.target.value)}
                      placeholder="For items not on list"
                    />
                  </td>
                  {/* Specification */}
                  <td style={tdS}>
                    <CellInput
                      value={it.specification}
                      onChange={e => updateItem(idx, 'specification', e.target.value)}
                      placeholder="Specification"
                    />
                  </td>
                  {/* Warehouse */}
                  <td style={tdS}>
                    <CellInput
                      value={it.warehouse}
                      onChange={e => updateItem(idx, 'warehouse', e.target.value)}
                      placeholder="Warehouse"
                    />
                  </td>
                  {/* Request Ref */}
                  <td style={tdS}>
                    <CellInput
                      value={it.request_ref}
                      onChange={e => updateItem(idx, 'request_ref', e.target.value)}
                      placeholder="Ref"
                    />
                  </td>
                  {/* BOQ Code */}
                  <td style={tdS}>
                    <CellInput
                      value={it.boq_item_code}
                      onChange={e => updateItem(idx, 'boq_item_code', e.target.value)}
                      placeholder="BOQ"
                    />
                  </td>
                  {/* Material Code */}
                  <td style={tdS}>
                    <CellInput
                      value={it.material_code}
                      onChange={e => updateItem(idx, 'material_code', e.target.value)}
                      placeholder="MAT-CODE"
                    />
                  </td>
                  {/* Rem. Rqst Qty */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.rem_rqst_qty} onChange={e => updateItem(idx, 'rem_rqst_qty', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                  </td>
                  {/* UOM */}
                  <td style={tdS}>
                    <Select
                      title="Unit of Measure"
                      value={it.unit_of_measure}
                      onChange={v => updateItem(idx, 'unit_of_measure', v)}
                      options={UOM_OPTIONS}
                      placeholder="— UOM —"
                      style={{ fontSize: 12, padding: '6px 9px' }}
                    />
                  </td>
                  {/* Qty */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.quantity_ordered} onChange={e => updateItem(idx, 'quantity_ordered', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                  </td>
                  {/* Last Pur.Pr — editable (may be auto-filled from material master) */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.last_pur_pr} onChange={e => updateItem(idx, 'last_pur_pr', e.target.value)} placeholder="0.00" style={{ textAlign: 'right', color: '#64748b' }} />
                  </td>
                  {/* Price (Unit Rate) */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.unit_rate} onChange={e => updateItem(idx, 'unit_rate', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} />
                  </td>
                  {/* Disc % */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.disc} onChange={e => updateItem(idx, 'disc', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                  </td>
                  {/* VAT % */}
                  <td style={tdS}>
                    <CellInput type="number" value={it.vat_percentage} onChange={e => updateItem(idx, 'vat_percentage', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                  </td>
                  {/* Total (Amount) */}
                  <td style={tdS}>
                    <div style={{ padding: '6px 9px', borderRadius: 7, background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1e40af', fontWeight: 700, fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {it.amount ? Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                    </div>
                  </td>
                  {/* Remark */}
                  <td style={tdS}>
                    <CellInput
                      value={it.remarks}
                      onChange={e => updateItem(idx, 'remarks', e.target.value)}
                      placeholder="Remark"
                    />
                  </td>
                  {/* Delete */}
                  <td style={{ ...tdS, width: 38, textAlign: 'center' }}>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        title="Remove line"
                        style={{ padding: '5px 7px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#fee2e2', color: '#dc2626', display: 'inline-flex', alignItems: 'center' }}
                      >
                        <IcoTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ background: '#f0f7ff', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '14px 22px', minWidth: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7, color: '#475569' }}>
              <span style={{ fontWeight: 600 }}>Subtotal</span>
              <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{fmt(totals.subtotal_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12, color: '#475569' }}>
              <span style={{ fontWeight: 600 }}>VAT</span>
              <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{fmt(totals.vat_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, borderTop: '1.5px solid #bfdbfe', paddingTop: 10, color: '#1e40af' }}>
              <span>Grand Total</span>
              <span style={{ fontFamily: 'monospace' }}>{fmt(totals.grand_total)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', paddingBottom: 8 }}>
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          style={{ padding: '10px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}
        >
          <IcoX /> Cancel
        </button>
        <button
          type="button"
          disabled={saving || items.length === 0}
          onClick={() => handleSave(false)}
          style={{ padding: '10px 20px', borderRadius: 9, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, background: '#d97706', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}
        >
          <IcoSave /> {saving ? 'Saving…' : 'Save as Draft'}
        </button>
        <button
          type="button"
          disabled={saving || prefetching || items.length === 0}
          onClick={async () => {
            if (!header.project_id)  return toast.show('Please select a project.', 'error');
            if (!header.supplier_id) return toast.show('Please select a supplier.', 'error');
            if (items.length === 0)  return toast.show('Add at least one line item.', 'error');
            if (backTo) { setConfirmSubmit(true); return; }
            setPrefetching(true);
            try {
              const res = await api.getEligibleApprovers('MATERIAL_PO', { grand_total: totals.grand_total });
              setPrefetchedStep(res || null);
            } catch {
              setPrefetchedStep(null);
            } finally {
              setPrefetching(false);
              setConfirmSubmit(true);
            }
          }}
          style={{ padding: '10px 22px', borderRadius: 9, border: 'none', cursor: (saving || prefetching) ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, background: '#16a34a', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, opacity: (saving || prefetching) ? 0.7 : 1 }}
        >
          {prefetching ? <><Spinner size={13} /> Preparing submission…</> : <><IcoSend /> {backTo ? `Resubmit to ${backTo}` : 'Submit for Approval'}</>}
        </button>
      </div>
    </div>
  );
}
